import { css } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

/**
 * Chrome shared by the editor's stacked modules — the boxed kind (notes) and the
 * bare kind (activity). Both draw the same header row, so the title, the count
 * link beside it, and the trailing action button live here rather than being
 * restated per module.
 *
 * Values come from the ShopEdit prototype's `.pgd-hdr-box` / `.pgd-details-sec`
 * / `.pgd-sec-hd` rules.
 */

/** A module that carries its own surface — white, fully bordered. */
export const FOCUSED_MODULE_BOX_STYLE = css`
  label: FOCUSED_MODULE_BOX_STYLE;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/**
 * A module with no box of its own — the section title sits on the page and each
 * entry below brings its own card. What the activity feed uses.
 */
export const FOCUSED_MODULE_SECTION_STYLE = css`
  label: FOCUSED_MODULE_SECTION_STYLE;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FOCUSED_MODULE_HEADER_STYLE = css`
  label: FOCUSED_MODULE_HEADER_STYLE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const FOCUSED_MODULE_HEADER_ACTIONS_STYLE = css`
  label: FOCUSED_MODULE_HEADER_ACTIONS_STYLE;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const FOCUSED_MODULE_TITLE_STYLE = css`
  label: FOCUSED_MODULE_TITLE_STYLE;
  margin: 0;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoStageNum)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.textSecondary};
`;

/**
 * The count trailing the title, e.g. "· 8 total notes". It inherits the title's
 * mono/caps treatment and only drops the weight, so the two read as one label
 * rather than a label with a button stuck to it — and the count is the way into
 * the full list, so there is no second "view all" control saying the same thing.
 */
export const FOCUSED_MODULE_COUNT_STYLE = css`
  label: FOCUSED_MODULE_COUNT_STYLE;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  font-weight: ${FONT_WEIGHT.regular};
  color: ${COLORS.textTertiary};
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  transition: color 0.12s;

  &:hover {
    color: ${COLORS.ink};
  }
`;

/** The trailing action in a module header — "+ Add", "Show all 12". */
export const FOCUSED_MODULE_ACTION_STYLE = css`
  label: FOCUSED_MODULE_ACTION_STYLE;
  flex-shrink: 0;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${COLORS.ink};
  background: ${COLORS.white};
  border: 1px solid ${COLORS.midtone};
  padding: 5px 10px;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;

  &:hover {
    background: ${COLORS.sunken};
  }
`;

/** Nothing-here state for a module — dashed, sunken, italic. */
export const FOCUSED_MODULE_EMPTY_STYLE = css`
  label: FOCUSED_MODULE_EMPTY_STYLE;
  padding: 20px;
  text-align: center;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  font-style: italic;
  color: ${COLORS.textTertiary};
  background: ${COLORS.sunken};
  border: 1px dashed ${COLORS.stroke};
`;

/**
 * The quieter empty state for a module that already has a box of its own (notes) —
 * a dashed inner box inside a bordered box reads as two frames around nothing.
 */
export const FOCUSED_MODULE_EMPTY_PLAIN_STYLE = css`
  label: FOCUSED_MODULE_EMPTY_PLAIN_STYLE;
  padding: 6px 0;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  font-style: italic;
  color: ${COLORS.textTertiary};
`;
